/**
 * Native camera bridge.
 *
 * Replaces the classic `<input type="file" accept="image/*" capture>` on
 * native so we get (a) true native camera UX with permission handling, (b)
 * EXIF metadata preserved (the web file-picker strips EXIF on iOS), and (c)
 * a direct `Blob` we can pipe into the existing upload `FormData` flow.
 *
 * On web (isNative() === false) the caller should fall back to the HTML
 * input element.
 */
import { Camera, CameraResultType, CameraSource } from "@capacitor/camera";
import { isNative } from "./platform";

/**
 * Launch the native camera + return a Blob ready to append to a FormData.
 * Rejects on cancel + on permission-denied so the caller can toast.
 *
 * @param source "camera" to open the capture UI; "photos" for the library.
 */
export async function captureNativePhoto(source = "camera") {
  if (!isNative()) {
    throw new Error("captureNativePhoto called on a web build");
  }
  const photo = await Camera.getPhoto({
    quality: 85,
    allowEditing: false,
    resultType: CameraResultType.Uri,
    source: source === "camera" ? CameraSource.Camera : CameraSource.Photos,
    saveToGallery: false,
    correctOrientation: true,
  });
  const webPath = photo.webPath;
  if (!webPath) throw new Error("Native camera returned no webPath");
  const res = await fetch(webPath);
  const blob = await res.blob();
  const ext = photo.format || "jpeg";
  const ts = Date.now();
  return {
    blob,
    mimeType: blob.type || `image/${ext}`,
    fileName: `claimscope-${ts}.${ext}`,
  };
}
